import { useCallback, useEffect } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useEventDetailsTaskActions(context) {
  const {
    TASK_COMMENT_REACTION_EMOJI_SET,
    applyEventTaskToState,
    canEditEventTasks,
    canViewEventTasks,
    confirmDialog,
    createTaskComment,
    createTaskDependency,
    createTaskItem,
    deleteTaskAttachment,
    deleteTaskDependency,
    deleteTaskItem,
    downloadTaskAttachment,
    ensurePrimaryEventTaskProject,
    eventId,
    eventTaskAttachmentInputRef,
    eventTaskCanEdit,
    eventTaskCommentReactionBusyMap,
    eventTaskCreating,
    eventTaskDependencyLinkType,
    eventTaskDetailCommentBody,
    eventTaskDetailForm,
    eventTaskDetailModal,
    eventTaskDetailTask,
    eventTaskNewSubtaskAssigneeId,
    eventTaskNewSubtaskDueOn,
    eventTaskNewSubtaskTitle,
    eventTaskProject,
    formatTaskDateInput,
    getEventTaskPrimaryAssignee,
    getPrimaryEventTaskProject,
    getTaskProjectDetail,
    isTaskDoneStatus,
    listTaskAttachments,
    listTaskComments,
    listTaskDependencies,
    normalizeEventTaskStatus,
    normalizeTaskPriority,
    removeEventTaskFromState,
    setEventSubtaskInlineEdit,
    setEventTaskAddingDependency,
    setEventTaskAddingDependencyTaskId,
    setEventTaskAllTasks,
    setEventTaskAllTasksLoading,
    setEventTaskAssignableAdmins,
    setEventTaskAttachments,
    setEventTaskAttachmentsLoading,
    setEventTaskCanEdit,
    setEventTaskCommentReactionBusyMap,
    setEventTaskCreating,
    setEventTaskCreatingSubtask,
    setEventTaskDeletingAttachmentId,
    setEventTaskDependencies,
    setEventTaskDependenciesLoading,
    setEventTaskDependencyLinkType,
    setEventTaskDependencyPickerFocused,
    setEventTaskDependencyQuery,
    setEventTaskDetailCommentBody,
    setEventTaskDetailComments,
    setEventTaskDetailCommentsLoading,
    setEventTaskDetailDeleting,
    setEventTaskDetailFeedTab,
    setEventTaskDetailForm,
    setEventTaskDetailModal,
    setEventTaskDetailPostingComment,
    setEventTaskDetailSaving,
    setEventTaskError,
    setEventTaskLoading,
    setEventTaskMetaEditField,
    setEventTaskNewSubtaskAssigneeId,
    setEventTaskNewSubtaskDueOn,
    setEventTaskNewSubtaskInlineEditField,
    setEventTaskNewSubtaskTitle,
    setEventTaskProject,
    setEventTaskRecentTasks,
    setEventTaskRemovingDependencyTaskId,
    setEventTaskSections,
    setEventTaskUpdatingId,
    setEventTaskUploadingAttachment,
    toastError,
    toastSuccess,
    toggleTaskCommentReaction,
    updateTaskItem,
    uploadTaskAttachment,
  } = context;
    const fetchEventTaskProject = useCallback(async () => {
      if (!eventId || !canViewEventTasks) {
        setEventTaskProject(null);
        setEventTaskRecentTasks([]);
        setEventTaskAllTasks([]);
        setEventTaskCanEdit(false);
        return;
      }
      setEventTaskLoading(true);
      setEventTaskError('');
      try {
        const json = await getPrimaryEventTaskProject(eventId);
        setEventTaskProject(json?.project || null);
        setEventTaskRecentTasks(Array.isArray(json?.recentTasks) ? json.recentTasks : []);
        setEventTaskCanEdit(Boolean(json?.canEdit) && canEditEventTasks);
      } catch (err) {
        console.error('Error fetching event task project:', err);
        setEventTaskProject(null);
        setEventTaskRecentTasks([]);
        setEventTaskAllTasks([]);
        setEventTaskCanEdit(false);
        setEventTaskError(err?.message || 'イベントタスク情報の取得に失敗しました');
      } finally {
        setEventTaskLoading(false);
      }
    }, [canEditEventTasks, canViewEventTasks, eventId]);

    const fetchEventTaskProjectTasks = useCallback(async (projectId) => {
      if (!projectId || !canViewEventTasks) {
        setEventTaskAllTasks([]);
        return;
      }
      setEventTaskAllTasksLoading(true);
      try {
        const json = await getTaskProjectDetail(projectId);
        const items = Array.isArray(json?.tasks) ? json.tasks : [];
        setEventTaskSections(Array.isArray(json?.sections) ? json.sections : []);
        setEventTaskAssignableAdmins(
          Array.isArray(json?.assignableAdmins) ? json.assignableAdmins : []
        );
        setEventTaskAllTasks(
          items.map((task) => ({
            ...task,
            status: String(task?.status || '').trim().toLowerCase() || 'todo',
            priority: normalizeTaskPriority(task?.priority),
          }))
        );
      } catch (err) {
        console.error('Error fetching event task list:', err);
        setEventTaskAllTasks([]);
        setEventTaskSections([]);
        setEventTaskAssignableAdmins([]);
      } finally {
        setEventTaskAllTasksLoading(false);
      }
    }, [canViewEventTasks]);

    const openEventTaskDetailModal = useCallback(
      (task) => {
        const taskId = String(task?.id || '').trim();
        const projectId = String(task?.projectId || eventTaskProject?.id || '').trim();
        if (!taskId || !projectId) return;
        setEventTaskDetailFeedTab('comments');
        setEventTaskMetaEditField('');
        setEventSubtaskInlineEdit({ taskId: '', field: '' });
        setEventTaskDetailCommentBody('');
        setEventTaskNewSubtaskTitle('');
        setEventTaskNewSubtaskDueOn('');
        setEventTaskNewSubtaskAssigneeId('');
        setEventTaskNewSubtaskInlineEditField('');
        setEventTaskDependencyQuery('');
        setEventTaskDependencyPickerFocused(false);
        setEventTaskDependencyLinkType('predecessor');
        setEventTaskDetailModal({ taskId, projectId });
      },
      [eventTaskProject?.id]
    );

    const closeEventTaskDetailModal = useCallback(() => {
      setEventTaskDetailModal(null);
      setEventTaskMetaEditField('');
      setEventSubtaskInlineEdit({ taskId: '', field: '' });
      setEventTaskDetailComments([]);
      setEventTaskDetailFeedTab('comments');
      setEventTaskDetailCommentBody('');
      setEventTaskNewSubtaskTitle('');
      setEventTaskNewSubtaskDueOn('');
      setEventTaskNewSubtaskAssigneeId('');
      setEventTaskNewSubtaskInlineEditField('');
      setEventTaskDependencies({ predecessors: [], successors: [] });
      setEventTaskDependencyQuery('');
      setEventTaskDependencyPickerFocused(false);
      setEventTaskDependencyLinkType('predecessor');
      setEventTaskAttachments([]);
      if (eventTaskAttachmentInputRef.current) {
        eventTaskAttachmentInputRef.current.value = '';
      }
      if (!eventTaskProject?.id) return;
      void fetchEventTaskProject();
      void fetchEventTaskProjectTasks(eventTaskProject.id);
    }, [eventTaskProject?.id, fetchEventTaskProject, fetchEventTaskProjectTasks]);

    useEffect(() => {
      if (canEditEventTasks) return;
      setEventTaskCanEdit(false);
    }, [canEditEventTasks]);

    useEffect(() => {
      if (canViewEventTasks) return;
      setEventTaskDetailModal(null);
    }, [canViewEventTasks]);

    useEffect(() => {
      if (!eventTaskDetailModal) return undefined;

      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          closeEventTaskDetailModal();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [eventTaskDetailModal, closeEventTaskDetailModal]);

    useEffect(() => {
      if (!eventTaskDetailTask) return;
      const assignee = getEventTaskPrimaryAssignee(eventTaskDetailTask);
      setEventTaskMetaEditField('');
      setEventSubtaskInlineEdit({ taskId: '', field: '' });
      setEventTaskNewSubtaskInlineEditField('');
      setEventTaskDetailForm({
        assigneeId: assignee?.adminUserId ? String(assignee.adminUserId) : '',
        startsOn: formatTaskDateInput(eventTaskDetailTask.startsOn),
        dueOn: formatTaskDateInput(eventTaskDetailTask.dueOn),
        status: normalizeEventTaskStatus(eventTaskDetailTask.status),
        priority: normalizeTaskPriority(eventTaskDetailTask.priority),
        description: String(eventTaskDetailTask.description || ''),
      });
    }, [eventTaskDetailTask, formatTaskDateInput]);

    const loadEventTaskDetailComments = useCallback(
      async (taskId) => {
        const targetId = String(taskId || '').trim();
        if (!targetId) {
          setEventTaskDetailComments([]);
          setEventTaskCommentReactionBusyMap({});
          return;
        }
        setEventTaskDetailCommentsLoading(true);
        try {
          const json = await listTaskComments(targetId);
          setEventTaskDetailComments(Array.isArray(json?.comments) ? json.comments : []);
          setEventTaskCommentReactionBusyMap({});
        } catch (err) {
          console.error('Error loading task comments:', err);
          setEventTaskDetailComments([]);
          setEventTaskCommentReactionBusyMap({});
          toastError(err?.message || 'コメント一覧の取得に失敗しました');
        } finally {
          setEventTaskDetailCommentsLoading(false);
        }
      },
      [toastError]
    );

    const loadEventTaskDetailDependencies = useCallback(
      async (taskId) => {
        const targetId = String(taskId || '').trim();
        if (!targetId) {
          setEventTaskDependencies({ predecessors: [], successors: [] });
          return;
        }
        setEventTaskDependenciesLoading(true);
        try {
          const json = await listTaskDependencies(targetId);
          setEventTaskDependencies({
            predecessors: (Array.isArray(json?.predecessors) ? json.predecessors : []).map((task) => ({
              ...task,
              status: normalizeEventTaskStatus(task?.status),
              priority: normalizeTaskPriority(task?.priority),
            })),
            successors: (Array.isArray(json?.successors) ? json.successors : []).map((task) => ({
              ...task,
              status: normalizeEventTaskStatus(task?.status),
              priority: normalizeTaskPriority(task?.priority),
            })),
          });
        } catch (err) {
          console.error('Error loading task dependencies:', err);
          setEventTaskDependencies({ predecessors: [], successors: [] });
          toastError(err?.message || '依存関係の取得に失敗しました');
        } finally {
          setEventTaskDependenciesLoading(false);
        }
      },
      [toastError]
    );

    const loadEventTaskDetailAttachments = useCallback(
      async (taskId) => {
        const targetId = String(taskId || '').trim();
        if (!targetId) {
          setEventTaskAttachments([]);
          return;
        }
        setEventTaskAttachmentsLoading(true);
        try {
          const json = await listTaskAttachments(targetId);
          setEventTaskAttachments(Array.isArray(json?.attachments) ? json.attachments : []);
        } catch (err) {
          console.error('Error loading task attachments:', err);
          setEventTaskAttachments([]);
          toastError(err?.message || '添付ファイル一覧の取得に失敗しました');
        } finally {
          setEventTaskAttachmentsLoading(false);
        }
      },
      [toastError]
    );

    useEffect(() => {
      const targetId = String(eventTaskDetailModal?.taskId || '').trim();
      if (!targetId) {
        setEventTaskDetailComments([]);
        setEventTaskCommentReactionBusyMap({});
        setEventTaskDependencies({ predecessors: [], successors: [] });
        setEventTaskAttachments([]);
        return;
      }
      setEventTaskDependencyQuery('');
      setEventTaskDependencyPickerFocused(false);
      setEventTaskDependencyLinkType('predecessor');
      void loadEventTaskDetailComments(targetId);
      void loadEventTaskDetailDependencies(targetId);
      void loadEventTaskDetailAttachments(targetId);
    }, [
      eventTaskDetailModal?.taskId,
      loadEventTaskDetailAttachments,
      loadEventTaskDetailComments,
      loadEventTaskDetailDependencies,
    ]);

    const ensureEventTaskProject = useCallback(async () => {
      if (!eventId || eventTaskCreating || !canViewEventTasks || !canEditEventTasks) return null;
      setEventTaskCreating(true);
      setEventTaskError('');
      try {
        const json = await ensurePrimaryEventTaskProject(eventId);
        const project = json?.project || null;
        setEventTaskProject(project);
        setEventTaskRecentTasks(Array.isArray(json?.recentTasks) ? json.recentTasks : []);
        setEventTaskCanEdit(Boolean(json?.canEdit) && canEditEventTasks);
        if (json?.created) {
          toastSuccess('イベントタスクを作成しました。');
        }
        return project;
      } catch (err) {
        console.error('Error ensuring event task project:', err);
        const message = err?.message || 'イベントタスクの作成に失敗しました';
        setEventTaskError(message);
        toastError(message);
        return null;
      } finally {
        setEventTaskCreating(false);
      }
    }, [canEditEventTasks, canViewEventTasks, eventId, eventTaskCreating, toastError, toastSuccess]);

    useEffect(() => {
      if (!eventTaskProject?.id) {
        setEventTaskAllTasks([]);
        setEventTaskSections([]);
        setEventTaskAllTasksLoading(false);
        return;
      }
      fetchEventTaskProjectTasks(eventTaskProject.id);
    }, [eventTaskProject?.id, fetchEventTaskProjectTasks]);

    const handleToggleEventTaskStatus = useCallback(
      async (task) => {
        if (!task?.id || !eventTaskProject?.id || !eventTaskCanEdit) return;
        const currentStatus = isTaskDoneStatus(task.status) ? 'done' : 'todo';
        const nextStatus = currentStatus === 'done' ? 'todo' : 'done';

        try {
          setEventTaskUpdatingId(task.id);
          const json = await updateTaskItem(String(task.id), { status: nextStatus });
          applyEventTaskToState(json?.task);
          const nextResolvedStatus = isTaskDoneStatus(json?.task?.status || nextStatus) ? 'done' : 'todo';

        toastSuccess(
          nextResolvedStatus === 'done'
            ? 'タスクを完了にしました。'
            : 'タスクを未着手に戻しました。'
        );
        } catch (err) {
          console.error('Error updating event task status:', err);
          toastError(err?.message || 'タスクステータスの更新に失敗しました');
        } finally {
          setEventTaskUpdatingId('');
          fetchEventTaskProjectTasks(eventTaskProject.id);
          fetchEventTaskProject();
        }
      },
      [
        applyEventTaskToState,
        eventTaskCanEdit,
        eventTaskProject?.id,
        fetchEventTaskProject,
        fetchEventTaskProjectTasks,
        toastError,
        toastSuccess,
      ]
    );

    const patchEventTaskInline = useCallback(
      async (taskId, payload, successMessage) => {
        const targetId = String(taskId || '').trim();
        if (!targetId || !eventTaskProject?.id || !eventTaskCanEdit) return;
        try {
          setEventTaskUpdatingId(targetId);
          const json = await updateTaskItem(targetId, payload || {});
          applyEventTaskToState(json?.task);
          if (successMessage) {
            toastSuccess(successMessage);
          }
        } catch (err) {
          console.error('Error updating event task inline:', err);
          toastError(err?.message || 'タスクの更新に失敗しました');
        } finally {
          setEventTaskUpdatingId('');
          await fetchEventTaskProjectTasks(eventTaskProject.id);
          await fetchEventTaskProject();
        }
      },
      [
        applyEventTaskToState,
        eventTaskCanEdit,
        eventTaskProject?.id,
        fetchEventTaskProject,
        fetchEventTaskProjectTasks,
        toastError,
        toastSuccess,
      ]
    );

    const handleSaveEventTaskDescription = useCallback(async () => {
      if (!eventTaskDetailTask?.id || !eventTaskCanEdit) return;
      try {
        setEventTaskDetailSaving(true);
        await patchEventTaskInline(
          eventTaskDetailTask.id,
          { description: String(eventTaskDetailForm.description || '').trim() || null },
          '説明を更新しました。'
        );
      } finally {
        setEventTaskDetailSaving(false);
      }
    }, [
      eventTaskCanEdit,
      eventTaskDetailForm.description,
      eventTaskDetailTask?.id,
      patchEventTaskInline,
    ]);

    const handleDeleteEventTaskDetail = useCallback(async () => {
      if (!eventTaskDetailTask?.id || !eventTaskCanEdit) return;
      if (!(await confirmDialog({ message: 'このタスクを削除しますか？', danger: true }))) return;
      try {
        setEventTaskDetailDeleting(true);
        await deleteTaskItem(String(eventTaskDetailTask.id));
        removeEventTaskFromState(eventTaskDetailTask.id);
        toastSuccess('タスクを削除しました。');
        closeEventTaskDetailModal();
      } catch (err) {
        console.error('Error deleting event task:', err);
        toastError(err?.message || 'タスクの削除に失敗しました');
      } finally {
        setEventTaskDetailDeleting(false);
      }
    }, [confirmDialog,
      closeEventTaskDetailModal,
      eventTaskCanEdit,
      eventTaskDetailTask?.id,
      removeEventTaskFromState,
      toastError,
      toastSuccess,
    ]);

    const handlePostEventTaskComment = useCallback(async (metadata = {}) => {
      const taskId = String(eventTaskDetailTask?.id || '').trim();
      const body = String(eventTaskDetailCommentBody || '').trim();
      if (!taskId || !body) return;
      try {
        setEventTaskDetailPostingComment(true);
        const json = await createTaskComment(taskId, { body, ...metadata });
        if (json?.comment) {
          setEventTaskDetailComments((prev) => [...prev, json.comment]);
        } else {
          await loadEventTaskDetailComments(taskId);
        }
        setEventTaskDetailCommentBody('');
      } catch (err) {
        console.error('Error posting task comment:', err);
        toastError(err?.message || 'コメント投稿に失敗しました');
      } finally {
        setEventTaskDetailPostingComment(false);
      }
    }, [eventTaskDetailCommentBody, eventTaskDetailTask?.id, loadEventTaskDetailComments, toastError]);

    const handleToggleEventTaskCommentReaction = useCallback(
      async (commentId, emoji) => {
        const targetCommentId = String(commentId || '').trim();
        const targetEmoji = String(emoji || '').trim();
        const taskId = String(eventTaskDetailTask?.id || '').trim();
        if (!taskId || !targetCommentId || !TASK_COMMENT_REACTION_EMOJI_SET.has(targetEmoji)) return;
        if (!eventTaskCanEdit) return;

        const pendingKey = `${targetCommentId}:${targetEmoji}`;
        if (eventTaskCommentReactionBusyMap[pendingKey]) return;

        try {
          setEventTaskCommentReactionBusyMap((prev) => ({ ...prev, [pendingKey]: true }));
          const json = await toggleTaskCommentReaction(targetCommentId, { emoji: targetEmoji });
          const nextReactions = Array.isArray(json?.comment?.reactions)
            ? json.comment.reactions
            : Array.isArray(json?.reactions)
              ? json.reactions
              : null;
          if (nextReactions) {
            setEventTaskDetailComments((prev) =>
              prev.map((comment) =>
                String(comment.id) === targetCommentId
                  ? { ...comment, reactions: nextReactions }
                  : comment
              )
            );
          } else {
            await loadEventTaskDetailComments(taskId);
          }
        } catch (err) {
          console.error('Error updating task comment reaction:', err);
          toastError(err?.message || 'コメントリアクションの更新に失敗しました');
        } finally {
          setEventTaskCommentReactionBusyMap((prev) => {
            if (!prev[pendingKey]) return prev;
            const next = { ...prev };
            delete next[pendingKey];
            return next;
          });
        }
      },
      [
        eventTaskCanEdit,
        eventTaskCommentReactionBusyMap,
        eventTaskDetailTask?.id,
        loadEventTaskDetailComments,
        toastError,
      ]
    );

    const handleCreateEventSubtask = useCallback(async () => {
      if (!eventTaskDetailTask?.id || !eventTaskProject?.id || !eventTaskCanEdit) return;
      const title = String(eventTaskNewSubtaskTitle || '').trim();
      if (!title) return;
      try {
        setEventTaskCreatingSubtask(true);
        const assigneeId = Number(eventTaskNewSubtaskAssigneeId || 0);
        const json = await createTaskItem({
            projectId: eventTaskProject.id,
            sectionId: eventTaskDetailTask.sectionId || null,
            parentTaskId: eventTaskDetailTask.id,
            title,
            status: 'todo',
            priority: 'normal',
            dueOn: eventTaskNewSubtaskDueOn || null,
            assigneeIds: Number.isInteger(assigneeId) && assigneeId > 0 ? [assigneeId] : [],
        });
        applyEventTaskToState(json?.task);
        setEventTaskNewSubtaskTitle('');
        setEventTaskNewSubtaskDueOn('');
        setEventTaskNewSubtaskAssigneeId('');
        setEventTaskNewSubtaskInlineEditField('');
        toastSuccess('サブタスクを追加しました。');
        await fetchEventTaskProjectTasks(eventTaskProject.id);
        await fetchEventTaskProject();
      } catch (err) {
        console.error('Error creating subtask:', err);
        toastError(err?.message || 'サブタスクの作成に失敗しました');
      } finally {
        setEventTaskCreatingSubtask(false);
      }
    }, [
      applyEventTaskToState,
      eventTaskCanEdit,
      eventTaskDetailTask?.id,
      eventTaskDetailTask?.sectionId,
      eventTaskNewSubtaskAssigneeId,
      eventTaskNewSubtaskDueOn,
      eventTaskNewSubtaskTitle,
      eventTaskProject?.id,
      fetchEventTaskProject,
      fetchEventTaskProjectTasks,
      toastError,
      toastSuccess,
    ]);

    const handleAddEventTaskDependency = useCallback(
      async (candidateTaskId) => {
        const selectedTaskId = String(eventTaskDetailTask?.id || '').trim();
        const targetTaskId = String(candidateTaskId || '').trim();
        if (!selectedTaskId || !targetTaskId || !eventTaskCanEdit) return;
        try {
          setEventTaskAddingDependency(true);
          setEventTaskAddingDependencyTaskId(targetTaskId);
          const successorTaskId =
            eventTaskDependencyLinkType === 'predecessor' ? selectedTaskId : targetTaskId;
          const predecessorTaskId =
            eventTaskDependencyLinkType === 'predecessor' ? targetTaskId : selectedTaskId;

          await createTaskDependency(successorTaskId, { predecessorTaskId });
          await loadEventTaskDetailDependencies(selectedTaskId);
          setEventTaskDependencyQuery('');
          setEventTaskDependencyPickerFocused(false);
          toastSuccess('依存関係を追加しました。');
        } catch (err) {
          console.error('Error adding event task dependency:', err);
          toastError(err?.message || '依存関係の追加に失敗しました');
        } finally {
          setEventTaskAddingDependency(false);
          setEventTaskAddingDependencyTaskId('');
        }
      },
      [
        eventTaskCanEdit,
        eventTaskDependencyLinkType,
        eventTaskDetailTask?.id,
        loadEventTaskDetailDependencies,
        toastError,
        toastSuccess,
      ]
    );

    const handleRemoveEventTaskDependency = useCallback(
      async (predecessorTaskId) => {
        const selectedTaskId = String(eventTaskDetailTask?.id || '').trim();
        const targetTaskId = String(predecessorTaskId || '').trim();
        if (!selectedTaskId || !targetTaskId || !eventTaskCanEdit) return;
        try {
          setEventTaskRemovingDependencyTaskId(targetTaskId);
          await deleteTaskDependency(selectedTaskId, targetTaskId);
          await loadEventTaskDetailDependencies(selectedTaskId);
          toastSuccess('依存関係を削除しました。');
        } catch (err) {
          console.error('Error removing event task dependency:', err);
          toastError(err?.message || '依存関係の削除に失敗しました');
        } finally {
          setEventTaskRemovingDependencyTaskId('');
        }
      },
      [
        eventTaskCanEdit,
        eventTaskDetailTask?.id,
        loadEventTaskDetailDependencies,
        toastError,
        toastSuccess,
      ]
    );

    const handleRemoveEventTaskSuccessor = useCallback(
      async (successorTaskId) => {
        const selectedTaskId = String(eventTaskDetailTask?.id || '').trim();
        const targetTaskId = String(successorTaskId || '').trim();
        if (!selectedTaskId || !targetTaskId || !eventTaskCanEdit) return;
        try {
          setEventTaskRemovingDependencyTaskId(`succ:${targetTaskId}`);
          await deleteTaskDependency(targetTaskId, selectedTaskId);
          await loadEventTaskDetailDependencies(selectedTaskId);
          toastSuccess('依存関係を削除しました。');
        } catch (err) {
          console.error('Error removing event task successor dependency:', err);
          toastError(err?.message || '依存関係の削除に失敗しました');
        } finally {
          setEventTaskRemovingDependencyTaskId('');
        }
      },
      [
        eventTaskCanEdit,
        eventTaskDetailTask?.id,
        loadEventTaskDetailDependencies,
        toastError,
        toastSuccess,
      ]
    );

    const handleUploadEventTaskAttachment = useCallback(
      async (event) => {
        const selectedTaskId = String(eventTaskDetailTask?.id || '').trim();
        const inputEl = event?.target;
        const file = inputEl?.files?.[0];
        if (!selectedTaskId || !file || !eventTaskCanEdit) {
          if (inputEl) inputEl.value = '';
          return;
        }
        try {
          setEventTaskUploadingAttachment(true);
          const json = await uploadTaskAttachment(selectedTaskId, { file });
          setEventTaskAttachments(Array.isArray(json?.attachments) ? json.attachments : []);
          toastSuccess('添付ファイルを追加しました。');
        } catch (err) {
          console.error('Error uploading event task attachment:', err);
          toastError(err?.message || '添付ファイルの登録に失敗しました');
        } finally {
          setEventTaskUploadingAttachment(false);
          if (inputEl) inputEl.value = '';
          if (eventTaskAttachmentInputRef.current) {
            eventTaskAttachmentInputRef.current.value = '';
          }
        }
      },
      [eventTaskCanEdit, eventTaskDetailTask?.id, toastError, toastSuccess]
    );

    const handleDeleteEventTaskAttachment = useCallback(
      async (attachmentId) => {
        const selectedTaskId = String(eventTaskDetailTask?.id || '').trim();
        const targetAttachmentId = String(attachmentId || '').trim();
        if (!selectedTaskId || !targetAttachmentId || !eventTaskCanEdit) return;
        if (!(await confirmDialog({ message: 'この添付ファイルを削除しますか？', danger: true }))) return;
        try {
          setEventTaskDeletingAttachmentId(targetAttachmentId);
          const json = await deleteTaskAttachment(targetAttachmentId);
          if (Array.isArray(json?.attachments)) {
            setEventTaskAttachments(json.attachments);
          } else {
            await loadEventTaskDetailAttachments(selectedTaskId);
          }
          toastSuccess('添付ファイルを削除しました。');
        } catch (err) {
          console.error('Error deleting event task attachment:', err);
          toastError(err?.message || '添付ファイルの削除に失敗しました');
        } finally {
          setEventTaskDeletingAttachmentId('');
        }
      },
      [confirmDialog,
        eventTaskCanEdit,
        eventTaskDetailTask?.id,
        loadEventTaskDetailAttachments,
        toastError,
        toastSuccess,
      ]
    );

    const handleDownloadEventTaskAttachment = useCallback(
      async (attachment) => {
        const targetAttachmentId = String(attachment?.id || '').trim();
        if (!targetAttachmentId) return;
        try {
          const blob = await downloadTaskAttachment(targetAttachmentId);
          const objectUrl = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = objectUrl;
          anchor.download = attachment?.fileName || 'attachment';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(objectUrl);
        } catch (err) {
          console.error('Error downloading event task attachment:', err);
          toastError(err?.message || '添付ファイルのダウンロードに失敗しました');
        }
      },
      [toastError]
    );
  return {
    fetchEventTaskProject,
    fetchEventTaskProjectTasks,
    openEventTaskDetailModal,
    closeEventTaskDetailModal,
    loadEventTaskDetailComments,
    loadEventTaskDetailDependencies,
    loadEventTaskDetailAttachments,
    ensureEventTaskProject,
    handleToggleEventTaskStatus,
    patchEventTaskInline,
    handleSaveEventTaskDescription,
    handleDeleteEventTaskDetail,
    handlePostEventTaskComment,
    handleToggleEventTaskCommentReaction,
    handleCreateEventSubtask,
    handleAddEventTaskDependency,
    handleRemoveEventTaskDependency,
    handleRemoveEventTaskSuccessor,
    handleUploadEventTaskAttachment,
    handleDeleteEventTaskAttachment,
    handleDownloadEventTaskAttachment,
  };
}